/**
 * Elite Modifier System
 *
 * Adds random modifiers to elite enemies that change their behavior
 * Similar to Path of Exile's rare monster modifiers
 */

export type EliteModifierId =
  | 'hizli'       // Fast - increased movement and attack speed
  | 'dayanikli'   // Tanky - increased HP and armor
  | 'canavar'     // Savage - increased damage
  | 'yildirici'   // Terrifying - causes fear effect
  | 'vampir'      // Vampire - life leech
  | 'buz'         // Frost - slows on hit
  | 'ates'        // Fire - burning damage
  | 'yildirim'    // Lightning - chain damage
  | 'cagiricr'    // Summoner - spawns minions
  | 'yansitici'   // Reflective - reflects damage
  | 'koruyucu'    // Shield - periodic invulnerability
  | 'patlayici'   // Explosive - explodes on death
  | 'saglikli'    // Regenerating - HP regen
  | 'teleport'    // Teleporter - can teleport
  | 'kacici';     // Evasive - high dodge chance

export interface EliteModifier {
  id: EliteModifierId;
  name: string;
  description: string;
  color: string;

  // Stat modifiers
  hpMultiplier?: number;
  damageMultiplier?: number;
  speedMultiplier?: number;
  armorMultiplier?: number;

  // Special effects
  onHitEffect?: 'slow' | 'burn' | 'shock' | 'fear';
  onDeathEffect?: 'explode' | 'spawn';
  periodicEffect?: 'regen' | 'shield' | 'teleport' | 'summon';

  // Experience bonus
  expMultiplier: number;

  // Loot bonus
  lootMultiplier: number;
}

export const ELITE_MODIFIERS: Record<EliteModifierId, EliteModifier> = {
  hizli: {
    id: 'hizli',
    name: 'Hızlı',
    description: 'Hareket ve saldırı hızı artmış',
    color: '#88ffff',
    speedMultiplier: 1.5,
    expMultiplier: 1.2,
    lootMultiplier: 1.1,
  },
  dayanikli: {
    id: 'dayanikli',
    name: 'Dayanıklı',
    description: 'Can ve zırh artmış',
    color: '#888888',
    hpMultiplier: 2.0,
    armorMultiplier: 1.5,
    expMultiplier: 1.3,
    lootMultiplier: 1.2,
  },
  canavar: {
    id: 'canavar',
    name: 'Canavar',
    description: 'Hasar çok artmış',
    color: '#ff4444',
    damageMultiplier: 1.75,
    expMultiplier: 1.25,
    lootMultiplier: 1.15,
  },
  yildirici: {
    id: 'yildirici',
    name: 'Yıldırıcı',
    description: 'Saldırıları korku verir',
    color: '#aa44aa',
    onHitEffect: 'fear',
    expMultiplier: 1.2,
    lootMultiplier: 1.1,
  },
  vampir: {
    id: 'vampir',
    name: 'Vampir',
    description: 'Verdiği hasarın bir kısmını can olarak alır',
    color: '#aa0000',
    hpMultiplier: 1.3,
    expMultiplier: 1.25,
    lootMultiplier: 1.15,
  },
  buz: {
    id: 'buz',
    name: 'Buzlu',
    description: 'Saldırıları yavaşlatır',
    color: '#aaffff',
    onHitEffect: 'slow',
    expMultiplier: 1.2,
    lootMultiplier: 1.1,
  },
  ates: {
    id: 'ates',
    name: 'Ateşli',
    description: 'Saldırıları yakar',
    color: '#ffaa44',
    damageMultiplier: 1.2,
    onHitEffect: 'burn',
    expMultiplier: 1.25,
    lootMultiplier: 1.15,
  },
  yildirim: {
    id: 'yildirim',
    name: 'Yıldırımlı',
    description: 'Saldırıları şok verir',
    color: '#ffff44',
    onHitEffect: 'shock',
    expMultiplier: 1.2,
    lootMultiplier: 1.1,
  },
  cagiricr: {
    id: 'cagiricr',
    name: 'Çağırıcı',
    description: 'Periyodik olarak minnion çağırır',
    color: '#44aa44',
    periodicEffect: 'summon',
    hpMultiplier: 1.2,
    expMultiplier: 1.4,
    lootMultiplier: 1.25,
  },
  yansitici: {
    id: 'yansitici',
    name: 'Yansıtıcı',
    description: 'Hasarın bir kısmını yansıtır',
    color: '#ff88ff',
    armorMultiplier: 1.3,
    expMultiplier: 1.3,
    lootMultiplier: 1.2,
  },
  koruyucu: {
    id: 'koruyucu',
    name: 'Koruyucu',
    description: 'Periyodik olarak kalkan oluşturur',
    color: '#4488ff',
    periodicEffect: 'shield',
    expMultiplier: 1.25,
    lootMultiplier: 1.15,
  },
  patlayici: {
    id: 'patlayici',
    name: 'Patlayıcı',
    description: 'Öldüğünde patlar',
    color: '#ff8844',
    onDeathEffect: 'explode',
    damageMultiplier: 0.8, // Lower damage since death is dangerous
    expMultiplier: 1.3,
    lootMultiplier: 1.2,
  },
  saglikli: {
    id: 'saglikli',
    name: 'Sağlıklı',
    description: 'Hızlı can yeniler',
    color: '#44ff44',
    periodicEffect: 'regen',
    hpMultiplier: 1.5,
    expMultiplier: 1.25,
    lootMultiplier: 1.15,
  },
  teleport: {
    id: 'teleport',
    name: 'Işınlayıcı',
    description: 'Aniden ışınlanabilir',
    color: '#aa88ff',
    periodicEffect: 'teleport',
    expMultiplier: 1.2,
    lootMultiplier: 1.1,
  },
  kacici: {
    id: 'kacici',
    name: 'Kaçıcı',
    description: 'Çok yüksek kaçış şansı',
    color: '#88ff88',
    speedMultiplier: 1.2,
    expMultiplier: 1.2,
    lootMultiplier: 1.1,
  },
};

// Get random modifiers for an elite enemy
export function rollEliteModifiers(count: number = 1): EliteModifier[] {
  const modifierIds = Object.keys(ELITE_MODIFIERS) as EliteModifierId[];
  const selected: EliteModifier[] = [];
  const usedIds = new Set<EliteModifierId>();

  while (selected.length < count && usedIds.size < modifierIds.length) {
    const randomIndex = Math.floor(Math.random() * modifierIds.length);
    const id = modifierIds[randomIndex];

    if (!usedIds.has(id)) {
      usedIds.add(id);
      selected.push(ELITE_MODIFIERS[id]);
    }
  }

  return selected;
}

// Calculate combined stats from modifiers
export function calculateModifierStats(modifiers: EliteModifier[]): {
  hpMultiplier: number;
  damageMultiplier: number;
  speedMultiplier: number;
  armorMultiplier: number;
  expMultiplier: number;
  lootMultiplier: number;
} {
  let hp = 1, damage = 1, speed = 1, armor = 1, exp = 1, loot = 1;

  for (const mod of modifiers) {
    if (mod.hpMultiplier) hp *= mod.hpMultiplier;
    if (mod.damageMultiplier) damage *= mod.damageMultiplier;
    if (mod.speedMultiplier) speed *= mod.speedMultiplier;
    if (mod.armorMultiplier) armor *= mod.armorMultiplier;
    exp *= mod.expMultiplier;
    loot *= mod.lootMultiplier;
  }

  return {
    hpMultiplier: hp,
    damageMultiplier: damage,
    speedMultiplier: speed,
    armorMultiplier: armor,
    expMultiplier: exp,
    lootMultiplier: loot,
  };
}

// Get modifier display info
export function getModifierDisplay(modifiers: EliteModifier[]): {
  names: string[];
  colors: string[];
  descriptions: string[];
} {
  return {
    names: modifiers.map(m => m.name),
    colors: modifiers.map(m => m.color),
    descriptions: modifiers.map(m => m.description),
  };
}
